/* eslint-disable unicorn/no-array-reduce */
import {
  call,
  take,
  takeEvery,
  takeLatest,
  select,
  put,
  debounce,
  all,
  race,
  delay,
  fork,
} from 'redux-saga/effects';

import { getLastValues, getSeries } from '../../services';
import { getCachedResponse, setCachedResponse } from '../../responseCache';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  CLEAR_SITE_DATA,
  INITIALIZE_POLLING,
  REFRESH_VALUES,
  setPollingActive,
  setPollingActiveDone,
} from '../application';
import { getRawSourceConnectivityById } from '../sources';
import {
  RECEIVE_LAST_SUBSCRIPTIONS,
  ADD_LAST_SUBSCRIPTIONS,
  FETCH_LAST_VARIABLE_DATA,
  SET_VARIABLES_LAST_POLLING,
  FETCH_EXTENDED_SERIES_DATA,
  STOP_FETCH_EXTENDED_SERIES_DATA,
  receiveLastValues,
} from './actions';
import { isFailedValue, getFailedVariableIds, FAILED_VARIABLE_RETRY_DELAYS_MS } from './helpers';

// Response cache - mirrors visualizations/sagas.js's responseCache pattern so both
// bundles cache last-value/series data the same way despite living in different bundles.
const responseCache = {
  lastValues: new Map(),
};

export function clearLastVariableDataCache() {
  responseCache.lastValues.clear();
}

// Last values have no aggregate/fill/date-range dependency - the key is just the variable id.
function getLastValueVariableKey(variableId) {
  return `last|${variableId}`;
}

// Silently retries just the variables whose chunk failed, patching recovered last values back
// into state. Same transient-failure rationale as the series equivalent.
function* retryFailedLastVariableIds(failedVariableIds, connection) {
  let remaining = failedVariableIds;

  for (const delayMs of FAILED_VARIABLE_RETRY_DELAYS_MS) {
    if (remaining.length === 0) return;
    yield delay(delayMs);

    let response;
    try {
      response = yield call(getLastValues, {
        variables: remaining.map((id) => ({ id })),
        connection,
      });
    } catch {
      continue; // network-level failure on the retry itself - try again on the next delay
    }

    const recovered = response.values.filter((value) => !isFailedValue(value));
    if (recovered.length > 0) {
      for (const value of recovered) {
        setCachedResponse(responseCache.lastValues, getLastValueVariableKey(value.variable), true);
      }
      yield put(receiveLastValues(recovered));
    }

    const stillFailingIds = new Set(getFailedVariableIds(response.values));
    remaining = remaining.filter((id) => stillFailingIds.has(id));
  }
}

// Called directly with a boolean (doFetchAllVariableData) or as a saga handler
// invoked with the triggering action (debounce(FETCH_LAST_VARIABLE_DATA, ...)),
// which never sets bypassCache, so it defaults to false there.
export function* doFetchLastVariableData(actionOrBypass = false) {
  const bypassCache = actionOrBypass === true;
  const subscriptions = yield select((state) => state.variables.lastSubscriptions);
  const subscribedVariableIds = Object.keys(subscriptions).filter(
    (variableId) => Object.keys(subscriptions[variableId]).length
  );

  if (subscribedVariableIds.length === 0) return;

  // The cached "data" is just a freshness marker - the actual last value already lives
  // in state.variables.lastData from the original fetch, so a cache hit only needs to
  // skip re-fetching, not re-supply the value.
  const variablesToFetch = bypassCache
    ? subscribedVariableIds
    : subscribedVariableIds.filter(
        (variableId) =>
          !getCachedResponse(responseCache.lastValues, getLastValueVariableKey(variableId))
      );

  if (variablesToFetch.length === 0) return;

  try {
    // connection tells the backend which sources are already known offline, so it skips
    // attempting them instead of waiting on a failure
    const connection = yield select(getRawSourceConnectivityById);
    const payload = {
      variables: variablesToFetch.map((variableId) => ({ id: variableId })),
      connection,
    };
    yield put({
      type: SET_VARIABLES_LAST_POLLING,
      data: variablesToFetch.reduce((acc, variable) => {
        acc[variable] = true;
        return acc;
      }, {}),
    });

    const { values } = yield call(getLastValues, payload);
    // Don't cache failed placeholders as if they were real freshness markers - they should be
    // refetched, and retryFailedLastVariableIds also caches recovered values when they arrive.
    for (const variableId of variablesToFetch) {
      setCachedResponse(responseCache.lastValues, getLastValueVariableKey(variableId), true);
    }
    yield put(receiveLastValues(values));

    const failedVariableIds = getFailedVariableIds(values);
    if (failedVariableIds.length > 0) {
      yield fork(retryFailedLastVariableIds, failedVariableIds, connection);
    }
  } catch (error) {
    console.error('Unable to fetch last variable data:', error);
    yield call(checkOnline);

    let errorMessage;
    if (error.response?.data?.code === 'SOURCE_CONNECTION_FAILED') {
      const isExternal = error.response?.data?.context?.isExternal;
      errorMessage = isExternal
        ? 'External connection timed out. Retrying at next polling interval'
        : 'Connection timed out. Retrying at next polling interval';
    } else {
      errorMessage = typeof error.message === 'object' ? error.message?.message : error.message;
    }

    yield put(displayNotification(getNotification('getLastVarValues', 'error')(errorMessage)));
  }
  yield put({
    type: SET_VARIABLES_LAST_POLLING,
    data: variablesToFetch.reduce((acc, variable) => {
      acc[variable] = false;
      return acc;
    }, {}),
  });
}

export function* doGetExtendedSeriesData(action) {
  // like get series for visualizations, but will use req_type to bypass interval checks in data-service
  // and return up to 10k data points for signal viewer or export
  const { startDate, endDate, variables, interval, aggregation, callback } = action;

  if (!variables?.length) return;

  try {
    const connection = yield select(getRawSourceConnectivityById);
    const payload = {
      from: startDate,
      to: endDate,
      interval,
      req_type: 'export_variable', // TODO: rename this to reflect the greater allowance on interval
      variables: variables.map((variable) => ({
        id: variable.id,
        aggregate: aggregation || variable.aggregate,
      })),
      connection,
    };
    const { values } = yield call(getSeries, payload);
    // Consider data points which has good quality
    const filteredValues = values.map((v) => ({
      ...v,
      values: v.values.map(([time, value, quality]) => [
        time,
        quality === 1 ? value : null,
        quality,
      ]),
    }));
    if (callback) {
      yield call(callback, filteredValues);
    }
  } catch (error) {
    console.error('Unable to export the variable data:', error);
    yield call(checkOnline);
    if (callback) {
      yield call(callback, { hasError: true });
    }

    let errorMessage;
    if (error.response?.data?.code === 'SOURCE_CONNECTION_FAILED') {
      const isExternal = error.response?.data?.context?.isExternal;
      errorMessage = isExternal
        ? 'External connection timed out. Retrying at next polling interval'
        : 'Connection timed out. Retrying at next polling interval';
    } else {
      errorMessage = typeof error.message === 'object' ? error.message?.message : error.message;
    }

    yield put(displayNotification(getNotification('getExtendedSeries', 'error')(errorMessage)));
  }
}

export function* doStartGetExtendedSeriesData(action) {
  yield race([call(doGetExtendedSeriesData, action), take(STOP_FETCH_EXTENDED_SERIES_DATA)]);
}

export function* doFetchAllVariableData(action) {
  // Subscriptions fetch their own data on mount.
  if (action?.isInitialLoad) return;

  yield put(setPollingActive('variables'));
  yield all([doFetchLastVariableData(true)]);
  yield put(setPollingActiveDone('variables'));
}

export function* doAddLastSubscriptions(action) {
  const { variableIds, panelId } = action;
  const subscriptions = yield select((state) => state.variables.lastSubscriptions);
  let shouldTriggerImmediateFetch = false;

  for (const variableId of variableIds) {
    if (
      !Object.keys(subscriptions).includes(variableId) ||
      Object.keys(subscriptions[variableId]).length === 0
    ) {
      shouldTriggerImmediateFetch = true;
    }
  }

  yield put({
    type: RECEIVE_LAST_SUBSCRIPTIONS,
    panelId,
    variableIds,
  });

  if (shouldTriggerImmediateFetch) {
    // New subscriptions, trigger data fetching.
    yield put({ type: FETCH_LAST_VARIABLE_DATA });
  }
}

export function* doClearLastVariableDataCache() {
  yield call(clearLastVariableDataCache);
}

export const sagas = [
  takeEvery(ADD_LAST_SUBSCRIPTIONS, doAddLastSubscriptions),
  takeLatest(FETCH_EXTENDED_SERIES_DATA, doStartGetExtendedSeriesData),
  debounce(500, FETCH_LAST_VARIABLE_DATA, doFetchLastVariableData),
  debounce(500, REFRESH_VALUES, doFetchAllVariableData),
  takeEvery(CLEAR_SITE_DATA, doClearLastVariableDataCache),
  // Last values must be current on site entry, since the initial refresh no longer refetches them.
  takeEvery(INITIALIZE_POLLING, doClearLastVariableDataCache),
];
